import { Component } from 'react';

import { lazyInject } from '../../../context/module';
import { container } from '../../../context/module';
import { FileUpload } from '../../../models/file-upload';
import { Logger } from '../../../models/logger';
import { FileManagerService } from '../../../services/file-manager.service';
import { MetricsService } from '../../../services/metrics.service';
import { ApiConfig } from '../../../services/api-config';
import { HelperService } from '../../../services/helper.service';
import { LightHttp } from '../../../services/light-http.service';
import { SettingsService } from '../../../services/settings.service';
import { checkUploadAvailability } from '../../../services/upload-availability.service';
import { ScriptLoadService } from '../../../services/script-load.service';
import { SpinnerService } from '../../../services/spinner.service';
import { MetricEventName } from '../../../types/metric-events';
import { PageIdentity, recentOnThisPage } from '../../../utils/recent-on-page';
import { untilDestroyed } from '../../../utils/until-destroyed';
import { needsAvailabilityCheck, UPLOAD_EXPIRY_OFF, UploadExpiryConfig, uploadExpiryConfigOf, X2_COPY } from '../../../utils/upload-expiry';
import { openStudioReedit } from '../../studio/export/reedit';
import { openReeditFlow } from '../../ui/file-upload-reedit/open-reedit-flow';
import styles from './recent-on-page.component.module.scss';
import Router from 'next/router';
import { downloadExistingResult } from '../rerun/download-existing';

interface RecentOnPageProps extends PageIdentity {
    /** Shown in the heading, e.g. "Compress GIF". */
    toolName?: string;
    pageSlug?: string;
    /** Tells the parent how many rows this will render, so one shared trigger can count them. */
    onCount?: (n: number) => void;
}

interface RecentOnPageState {
    files: FileUpload[];
    /** FIX-2 T2: the row whose Re-edit is asking the server (its key reads "Checking…"). */
    checkingId?: string | null;
    /** FIX-2 T2: rows the server said are gone -- the row says so in place of its key. */
    expiredIds?: string[];
}

/**
 * "You converted these here — change a setting and run it again."
 *
 * Re-edit re-stages an already-uploaded file with different options and re-runs it. It is the
 * single most common thing a user wants next — 96.5% of continuations are same-tool re-runs,
 * not switches to another tool (docs/research/next-tool-transitions.md) — and it existed only
 * on `/downloads`, mixed in with every other file in the session. Someone who wants a slightly
 * smaller GIF had to leave the page whose settings they were just using, find their file among
 * the rest, and edit it there.
 *
 * Rendered as a section of the recent-files panel rather than a block of its own. As a separate
 * block it sat below the tool with its own heading and border, so a returning visitor met two
 * near-identical panels -- this one and "Files you used recently" -- doing different things under
 * labels that did not distinguish them. One trigger, two labelled sections.
 * See docs/proposed/RETENTION_FIXES.md T3.3.
 */
export class RecentOnPageComponent extends Component<RecentOnPageProps, RecentOnPageState> {
    private readonly logger = Logger.getLogger('RecentOnPageComponent');

    @lazyInject(FileManagerService) private fileManagerService: FileManagerService;
    @lazyInject(ScriptLoadService) private scriptLoadService: ScriptLoadService;
    @lazyInject(SpinnerService) private spinner: SpinnerService;
    @lazyInject(SettingsService) private settingsService: SettingsService;
    @lazyInject(LightHttp) private lightHttp: LightHttp;
    @lazyInject(ApiConfig) private apiConfig: ApiConfig;
    @lazyInject(HelperService) private helperService: HelperService;

    readonly state: Readonly<RecentOnPageState> = { files: [] };

    /** FIX-2: the expired-upload gate (off = today's list, no availability call). */
    private uploadExpiry: UploadExpiryConfig = UPLOAD_EXPIRY_OFF;
    private lastFileUploads: FileUpload[] = [];
    /** A second press while the check runs does nothing. */
    private checkInFlight = false;
    /** X2-1 for the row that just turned out to be gone, in a polite region mounted before it arrives. */
    private announce = '';
    /** The row whose Re-edit key just gave way to X2-1: focus moves to the line so it is not dropped on <body>. */
    private focusExpiredId: string | null = null;

    private get identity(): PageIdentity {
        return {
            processType: this.props.processType,
            toType: this.props.toType,
            uniqueProcessType: this.props.uniqueProcessType,
        };
    }

    private applyExpiry(flags: unknown) {
        const next = uploadExpiryConfigOf(flags);
        const prev = this.uploadExpiry;
        if (next.enabled === prev.enabled && next.retentionMinutes === prev.retentionMinutes && next.checkAfterMinutes === prev.checkAfterMinutes) return;
        this.uploadExpiry = next;
        this.refresh(this.lastFileUploads);
    }

    componentDidMount() {
        try {
            this.settingsService?.featureFlags?.pipe(untilDestroyed(this)).subscribe({
                next: (ff) => this.applyExpiry(ff),
                error: () => undefined,
            });
        } catch { /* the list works without the gate */ }
        this.refresh(this.fileManagerService.getFileUploads());
        this.fileManagerService.fileUploadsListChanged
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (fileUploads) => this.refresh(fileUploads),
                error: (e) => this.logger.error({ message: 'Recent-on-page list failed', error: e }),
            });
    }

    componentWillUnmount() {
        // for untilDestroyed
    }

    private refresh(fileUploads: FileUpload[]) {
        this.lastFileUploads = fileUploads || [];
        const files = recentOnThisPage(fileUploads, this.identity, undefined, { config: this.uploadExpiry });
        // Only re-render when the set actually changes: fileUploadsListChanged fires on every
        // progress tick during an upload, and this block sits under a live tool.
        const same = files.length === this.state.files.length
            && files.every((f, i) => f.id === this.state.files[i]?.id);
        if (!same) {
            this.setState({ files });
            this.props.onCount?.(files.length);
        }
    }

    private async reedit(fileUpload: FileUpload) {
        try {
            container.get(MetricsService).emit(MetricEventName.RECENT_REEDIT_CLICKED, {
                pageSlug: this.props.pageSlug || '',
                toType: `${fileUpload.toType || ''}`,
            });
        } catch { /* telemetry is never load-bearing */ }

        // Prod fixes 2026-10-02 (PLAN.md C6): the one Re-edit flow, shared with the /downloads card and
        // "Make it smaller". This row's own routing and loader are passed in, unchanged.
        await openReeditFlow(fileUpload, {
            // §11.0 R11 — same branch as the /downloads card, same helper, same answer: a Studio
            // workflow re-opens in the Studio, not in the merge modal. FIX-2 T2 (T3): then the same
            // availability check as the /downloads card's Re-edit, before the modal.
            route: async (fu) => openStudioReedit(fu) || await this.uploadGone(fu),
            // Re-edit re-runs a conversion, which needs the upload/websocket stack the tool page
            // only loads lazily. Without this the modal opens onto a dead Apply button.
            prepare: async () => {
                await this.spinner.runWithLoader(async () => {
                    try {
                        await this.scriptLoadService.loadAllScriptsIfNotLoaded();
                    } catch (e) {
                        this.logger.error({ message: 'Something went wrong while loading scripts.', error: e });
                    }
                }, 'Please wait while we initialize file upload service...');
            },
            // FIX-8 (UX-SPEC 8.4): no result card is mounted on the tool page, so "Download result"
            // takes the card's one-file Download path directly; a many-file result goes to /downloads.
            onDownloadExisting: () => {
                if (!downloadExistingResult(fileUpload)) Router.push('/downloads');
            },
        });
    }

    /**
     * FIX-2 T2 (UX-SPEC §2.1): true when the modal must not open. Only an upload at least `uploadCheckAfterMinutes` old
     * is asked about (<= 3 s, fail open). Missing -> the row says X2-1 in place of its key, announced politely.
     */
    async uploadGone(fileUpload: FileUpload): Promise<boolean> {
        if (!this.uploadExpiry.enabled) return false;
        if (this.checkInFlight) return true;
        if (!needsAvailabilityCheck(fileUpload as any, this.uploadExpiry)) return false;
        this.checkInFlight = true;
        this.setState({ checkingId: fileUpload.id });
        let outcome: 'present' | 'missing' | 'check_failed' = 'check_failed';
        try {
            let headers: Record<string, string> = {};
            try { headers = this.helperService.getCommonHttpHeaders() || {}; } catch { /* unauthenticated */ }
            outcome = await checkUploadAvailability(fileUpload as any, this.lightHttp, this.apiConfig.getUploadsAvailabilityEndpoint(), headers);
        } catch {
            outcome = 'check_failed';
        } finally {
            this.checkInFlight = false;
        }
        try {
            container.get(MetricsService).emit(MetricEventName.REEDIT_AVAILABILITY_CHECK, {
                outcome, page: this.props.pageSlug || '', surface: 'rerun_row',
            });
        } catch { /* never load-bearing */ }
        if (outcome === 'missing') {
            this.announce = X2_COPY.expiredLine;
            this.focusExpiredId = fileUpload.id;
            this.setState({ checkingId: null, expiredIds: [...(this.state.expiredIds || []), fileUpload.id] });
            return true;
        }
        this.setState({ checkingId: null });
        return false;
    }

    render() {
        const { files } = this.state;
        if (files.length === 0) return null;
        const expiredIds = this.state.expiredIds || [];

        // A section of the recent-files panel, not a panel of its own. It used to render its own
        // heading and border further down the page, so a returning visitor met two near-identical
        // blocks doing different things under labels that did not distinguish them.
        return (
            <div className={styles.recent}>
                <h3 className={styles.heading}>Re-run a conversion from this page</h3>
                <p className={styles.sub}>
                    Change a setting and run it again — no need to upload anything.
                </p>
                <ul className={styles.list}>
                    {files.map(f => (
                        <li key={f.id} className={styles.row}>
                            <span className={styles.name}>{f.name}</span>
                            {expiredIds.includes(f.id)
                                // FIX-2 X2-1: the tool page IS where they re-upload, so the line is the whole answer.
                                ? <span className={styles.expired} data-jkey="recent-reedit-expired" tabIndex={-1}
                                    ref={(el) => {
                                        if (el && this.focusExpiredId === f.id) {
                                            this.focusExpiredId = null;
                                            try { el.focus(); } catch { /* focus is a courtesy */ }
                                        }
                                    }}>{X2_COPY.expiredLine}</span>
                                : <button type="button" className={styles.action}
                                    aria-busy={this.state.checkingId === f.id ? true : undefined}
                                    onClick={() => { if (!this.state.checkingId) this.reedit(f); }}>
                                    {this.state.checkingId === f.id ? X2_COPY.checking : 'Re-edit'}
                                </button>}
                        </li>
                    ))}
                </ul>
                {this.uploadExpiry.enabled && <p className="sr-only" aria-live="polite" data-jkey="recent-reedit-live">{this.announce}</p>}
            </div>
        );
    }
}
